import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import type { BatteryActivityType, ChartSegment, EnrichedForecastPoint } from '../../../utils/socChartUtils';
import { generateTooltipContent } from '../../../utils/socChartUtils';

import type { NowToFutureLink } from './socChartDerivations';
import type { TariffRateView } from './SocChartToggles';
import type { ShadingBlocks } from './useTariffShadingBlocks';

type ChartDataPoint = {
  x: string;
  y: number;
  _ts?: number;
  command?: string;
  startTime?: string;
  _previousSoc?: number;
  [key: string]: unknown;
};

// Typed shape for Victory datum callbacks
type VictoryDatum = {
  x?: string;
  y?: number;
  _ts?: number;
  _skipTooltip?: boolean;
  _series?: string;
  _solarKwh?: number;
  command?: string;
  startTime?: string;
  _previousSoc?: number;
  _activityType?: BatteryActivityType;
};

interface SocChartProps {
  chartAvailable: boolean;
  loopIsDisabled: boolean;
  showBatteryActions: boolean;
  showSolarGeneration: boolean;
  showTariffRates: boolean;
  tariffRateView: TariffRateView;
  behaviorChartData: ChartDataPoint[] | null | undefined;
  historicChartData: ChartDataPoint[] | null | undefined;
  historicChartSegments: ChartSegment[];
  futureChartSegments: ChartSegment[] | null | undefined;
  nowToFutureLink: NowToFutureLink | null;
  safeActiveTariffShadingBlocks: ShadingBlocks;
  solarLineData: Array<{ x: string; y: number; _series: string; _solarKwh: number }>;
  solarLineDataLeft: Array<{ x: string; y: number; _series: string; _solarKwh: number }>;
  solarMaxKwh: number;
  tooltipBlacklist: string[];
  intersectionX: string | null | undefined;
  intersectionY: number;
  batteryCapacityKwh: number | undefined;
  placeholderXAxis: string[];
  nowOverrideSecs?: number;
  height?: number;
  width?: number;
}

const SocChart: React.FC<SocChartProps> = ({
  chartAvailable,
  loopIsDisabled,
  showBatteryActions,
  showSolarGeneration,
  showTariffRates,
  tariffRateView,
  behaviorChartData,
  historicChartData,
  historicChartSegments,
  futureChartSegments,
  nowToFutureLink,
  safeActiveTariffShadingBlocks,
  solarLineData,
  solarLineDataLeft,
  solarMaxKwh,
  tooltipBlacklist,
  intersectionX,
  intersectionY,
  batteryCapacityKwh,
  placeholderXAxis,
  nowOverrideSecs,
  height = 278,
  width,
}) => {
  const theme = useTheme();

  const _chartW = width ?? 450;
  const _leftPad = 70;
  const _rightPad = 70;
  const _nowX = _chartW / 2;
  const prevLabelX = (_leftPad + _nowX) / 2;
  const nextLabelX = (_nowX + (_chartW - _rightPad)) / 2;
  const sectionLabelY = height - 8;

  const lastDataTs = historicChartData?.[historicChartData.length - 1]?._ts ?? null;

  const computeNowLabel = useCallback(() => {
    if (!lastDataTs) return 'Now';
    const nowSecs = nowOverrideSecs ?? Date.now() / 1000;
    const diffMins = Math.round((nowSecs - lastDataTs) / 60);
    if (diffMins < 60) return 'Now';
    const hrs = diffMins / 60;
    // Round to nearest 0.5hr
    const rounded = Math.round(hrs * 2) / 2;
    return `${rounded}hr ago`;
  }, [lastDataTs, nowOverrideSecs]);

  const [nowLabel, setNowLabel] = useState(computeNowLabel);

  useEffect(() => {
    setNowLabel(computeNowLabel());
    const id = setInterval(() => setNowLabel(computeNowLabel()), 60_000);
    return () => clearInterval(id);
  }, [computeNowLabel]);

  // Snapped reference position: find the latest data point whose _ts is <= now (floor).
  // This means 12:50 snaps to 12:30 rather than 13:00, so the stale reference line only
  // appears once we've already passed that interval. Using an existing tick ensures
  // Victory's categorical scale positions the line correctly.
  const staleReferenceX = useMemo(() => {
    if (nowLabel === 'Now' || !behaviorChartData?.length) return null;
    const nowSecs = nowOverrideSecs ?? Date.now() / 1000;
    let best: ChartDataPoint | null = null;
    for (const point of behaviorChartData) {
      if (typeof point._ts === 'number' && point._ts <= nowSecs) {
        if (!best || point._ts > (best._ts as number)) {
          best = point;
        }
      }
    }
    return best?.x ?? null;
  }, [behaviorChartData, nowLabel, nowOverrideSecs]);

  // Only show the stale reference x-axis label when data is >= 1.5hr stale, to avoid clustering.
  const showStaleReferenceLabel = useMemo(() => {
    if (!lastDataTs || !staleReferenceX) return false;
    const nowSecs = nowOverrideSecs ?? Date.now() / 1000;
    return nowSecs - lastDataTs >= 90 * 60;
  }, [lastDataTs, staleReferenceX, nowOverrideSecs]);

  // (same tick as the last historic point — the historic point handles the tooltip there).
  const shadingBoundaryXValues = useMemo(() => {
    const allBlocks = [...safeActiveTariffShadingBlocks.cheap, ...safeActiveTariffShadingBlocks.peak];
    const xs = new Set<string>();
    allBlocks.forEach((block) => {
      if (block.length > 0) xs.add(block[0].x);
    });
    return Array.from(xs);
  }, [safeActiveTariffShadingBlocks]);

  const patchedFutureChartSegments = useMemo(() => {
    if (!futureChartSegments?.length || !intersectionX) return futureChartSegments;
    const first = futureChartSegments[0];
    if (!first?.data?.length || first.data[0].x !== intersectionX) return futureChartSegments;
    return [
      { ...first, data: [{ ...first.data[0], _skipTooltip: true }, ...first.data.slice(1)] },
      ...futureChartSegments.slice(1),
    ];
  }, [futureChartSegments, intersectionX]);

  return (
    <VictoryChart
      height={height}
      {...(width !== undefined ? { width } : {})}
      padding={{ top: 10, bottom: 28, left: 70, right: 70 }}
      domain={{ y: [-5, 120] as [number, number] }}
      containerComponent={
        <VictoryVoronoiContainer
          voronoiBlacklist={tooltipBlacklist}
          // Victory's labels type requires string, but null suppresses the tooltip entirely.
          labels={
            (({ datum }: { datum: VictoryDatum }) => {
              if (datum._skipTooltip) return null;
              if (datum._series === 'solarGeneration') {
                return `Time: ${datum.x ?? ''}\nSolar generation: ${(datum._solarKwh ?? 0).toFixed(2)} kWh`;
              }
              if (datum.command && datum.startTime) {
                return generateTooltipContent(
                  datum as unknown as EnrichedForecastPoint,
                  batteryCapacityKwh,
                  datum._previousSoc
                );
              }
              return `Time: ${datum.x ?? ''}\nState of charge: ${Math.round(datum.y ?? 0)}%`;
            }) as unknown as (props: { datum: VictoryDatum }) => string
          }
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea={true}
              cornerRadius={4}
              pointerLength={0}
              style={{ fontSize: 12, lineHeight: 1.3 }}
              flyoutStyle={{
                fill: theme.colors.white,
                stroke: theme.colors.light,
                strokeWidth: 1,
              }}
              flyoutPadding={{ top: 8, bottom: 8, left: 8, right: 8 }}
            />
          }
        />
      }
    >
      {/* Left Y axis: battery state of charge */}
      {chartAvailable && (
        <VictoryAxis
          dependentAxis
          tickValues={[0, 25, 50, 75, 100]}
          label="Battery state of charge (%)"
          style={{
            axis: { stroke: 'transparent' },
            ticks: { stroke: 'transparent' },
            tickLabels: {
              fill: theme.colors.black,
              fontSize: 10,
              padding: 5,
              fontWeight: 'normal',
            },
            axisLabel: {
              fontSize: 12,
              padding: 35,
              angle: -90,
              textAnchor: 'middle',
              fill: theme.colors.black,
              fontWeight: 'normal',
            },
            grid: { stroke: 'none' },
          }}
        />
      )}

      {/* Right axis for solar generation (kWh), plotted as a normalised series */}
      {chartAvailable && showSolarGeneration && solarMaxKwh > 0 && (
        <VictoryAxis
          dependentAxis
          orientation="right"
          crossAxis={false}
          tickValues={[0, 25, 50, 75, 100]}
          tickFormat={(t) => {
            const value = (t * solarMaxKwh) / 100;
            if (solarMaxKwh < 1) return value.toFixed(2);
            if (solarMaxKwh < 10) return value.toFixed(1);
            return `${Math.round(value)}`;
          }}
          label="Solar generation (kWh)"
          style={{
            axis: { stroke: 'transparent' },
            ticks: { stroke: 'transparent' },
            tickLabels: {
              fill: theme.colors.black,
              fontSize: 10,
              padding: 5,
              fontWeight: 'normal',
            },
            axisLabel: {
              fontSize: 12,
              padding: 45,
              angle: 90,
              textAnchor: 'middle',
              fill: theme.colors.black,
              fontWeight: 'normal',
            },
            grid: { stroke: 'none' },
          }}
        />
      )}

      {chartAvailable &&
        showTariffRates &&
        safeActiveTariffShadingBlocks.peak.map((block, idx) => (
          <VictoryArea
            key={`peak-${tariffRateView}-${idx}`}
            name={`peakShading-${idx}`}
            data={block}
            interpolation="stepAfter"
            style={{
              data: {
                fill:
                  tariffRateView === 'import'
                    ? '#E9EDF7' // import peak
                    : tariffRateView === 'export'
                      ? '#FFEEA0' // export peak
                      : theme.colors.peak,
                fillOpacity: 1,
                stroke: 'none',
              },
            }}
          />
        ))}
      {chartAvailable &&
        showTariffRates &&
        safeActiveTariffShadingBlocks.cheap.map((block, idx) => (
          <VictoryArea
            key={`cheap-${tariffRateView}-${idx}`}
            name={`cheapShading-${idx}`}
            data={block}
            interpolation="stepAfter"
            style={{
              data: {
                fill:
                  tariffRateView === 'import'
                    ? '#EBF7F2' // import cheap
                    : tariffRateView === 'export'
                      ? '#FFE7E2' // export cheap
                      : 'rgba(60, 186, 146, 0.2)',
                fillOpacity: 1,
                stroke: 'none',
              },
            }}
          />
        ))}
      {chartAvailable &&
        showTariffRates &&
        shadingBoundaryXValues.map((x) => (
          <VictoryLine
            key={`tariff-boundary-${x}`}
            name={`tariffBoundary-${x}`}
            data={[
              { x, y: 0 },
              { x, y: 100 },
            ]}
            style={{ data: { stroke: 'white', strokeWidth: 1 } }}
          />
        ))}

      {chartAvailable && behaviorChartData && behaviorChartData.length > 1 && (
        <>
          {[25, 50, 75, 100].map((y) => (
            <Fragment key={`grid-${y}`}>
              <VictoryLine
                name={`customGridLeft-${y}`}
                data={[
                  { x: behaviorChartData[0].x, y },
                  { x: intersectionX, y },
                ]}
                style={{ data: { stroke: theme.colors.grey, strokeWidth: 1 } }}
              />
              <VictoryLine
                name={`customGridRight-${y}`}
                data={[
                  { x: intersectionX, y },
                  { x: behaviorChartData[behaviorChartData.length - 1].x, y },
                ]}
                style={{ data: { stroke: theme.colors.grey, strokeWidth: 1, strokeDasharray: '6,4' } }}
              />
            </Fragment>
          ))}

          {/* X-axis baseline: solid left, dotted right */}
          <VictoryLine
            name="xAxisLineLeft"
            data={[
              { x: behaviorChartData[0].x, y: 0 },
              { x: intersectionX, y: 0 },
            ]}
            style={{ data: { stroke: theme.colors.light, strokeWidth: 2 } }}
          />
          <VictoryLine
            name="xAxisLineRight"
            data={[
              { x: intersectionX, y: 0 },
              { x: behaviorChartData[behaviorChartData.length - 1].x, y: 0 },
            ]}
            style={{ data: { stroke: theme.colors.light, strokeWidth: 2, strokeDasharray: '6,4' } }}
          />
        </>
      )}

      {/* Solar generation: solid historic line */}
      {chartAvailable && showSolarGeneration && solarLineDataLeft.length > 1 && (
        <VictoryLine
          name="solarGenerationLineCopy"
          data={solarLineDataLeft}
          style={{ data: { stroke: theme.colors.solar, strokeWidth: 2 } }}
        />
      )}

      {/* Solar generation: dashed forecast line */}
      {chartAvailable && showSolarGeneration && solarLineData.length > 1 && (
        <VictoryLine
          name="solarGenerationLine"
          data={solarLineData}
          style={{ data: { stroke: theme.colors.solar, strokeWidth: 2, strokeDasharray: '6,4' } }}
        />
      )}

      {/* Bridge from "Now" to first future segment */}
      {chartAvailable &&
        !loopIsDisabled &&
        showBatteryActions &&
        nowToFutureLink &&
        Array.isArray(nowToFutureLink.data) &&
        nowToFutureLink.data.length >= 2 && (
          <VictoryLine
            name="nowToFutureLink"
            data={nowToFutureLink.data}
            style={{
              data: {
                stroke: theme.colors.batteryActivity[nowToFutureLink.activityType] || theme.colors.secondary,
                strokeDasharray: '6,4',
                strokeWidth: 2,
              },
            }}
          />
        )}

      {/* Colour-coded future plan segments */}
      {chartAvailable &&
        !loopIsDisabled &&
        showBatteryActions &&
        patchedFutureChartSegments?.map((segment, idx) => (
          <VictoryLine
            key={`segment-${idx}`}
            name={`segment-${idx}`}
            data={segment.data}
            style={{
              data: {
                stroke: theme.colors.batteryActivity[segment.activityType],
                strokeDasharray: '6,4',
                strokeWidth: 2,
              },
            }}
          />
        ))}

      {/* Future plan fallback: single dashed line */}
      {chartAvailable && !loopIsDisabled && showBatteryActions && !patchedFutureChartSegments?.length && (
        <VictoryLine
          name="futurePlan"
          data={behaviorChartData ?? []}
          style={{ data: { stroke: theme.colors.secondary, strokeDasharray: '6,4', strokeWidth: 2 } }}
        />
      )}

      {/* Historic SOC: colour-coded segments */}
      {chartAvailable &&
        showBatteryActions &&
        historicChartSegments.length > 0 &&
        historicChartSegments.map((segment, idx) => (
          <VictoryLine
            key={`historic-segment-${idx}`}
            name={`historic-segment-${idx}`}
            data={segment.data}
            style={{ data: { stroke: theme.colors.batteryActivity[segment.activityType], strokeWidth: 2 } }}
          />
        ))}

      {/* Historic SOC fallback: single solid line when actions hidden */}
      {chartAvailable && !showBatteryActions && (
        <VictoryLine
          name="historicChart"
          data={historicChartData ?? []}
          style={{ data: { stroke: theme.colors.secondary, strokeWidth: 2 } }}
        />
      )}

      {/* X axis */}
      <VictoryAxis
        tickValues={behaviorChartData ? behaviorChartData.map((d) => d.x) : placeholderXAxis}
        tickFormat={(t: string, index: number, ticks: string[]) => {
          if (!chartAvailable) return '';
          if (index === 0 || index === ticks.length - 1 || t === intersectionX) return t;
          if (showStaleReferenceLabel && t === staleReferenceX) return t;
          return '';
        }}
        style={{
          ticks: { stroke: chartAvailable ? theme.colors.light : 'transparent', size: 5 },
          tickLabels: { fill: theme.colors.black, fontSize: 10, padding: 5 },
          axis: { stroke: chartAvailable ? theme.colors.light : 'transparent' },
        }}
      />

      {/* Dotted grey stale reference line — shows current time when data is stale (rendered before nowLine so it appears behind) */}
      {chartAvailable && nowLabel !== 'Now' && (
        <VictoryLine
          name="staleReferenceLine"
          data={[
            { x: staleReferenceX, y: -5 },
            { x: staleReferenceX, y: 120 },
          ]}
          style={{
            data: {
              stroke: theme.colors.grey,
              strokeWidth: 1.5,
              strokeDasharray: '4,4',
              fillOpacity: 0,
            },
          }}
        />
      )}

      {/* "Now" vertical line with pill label (at last data point) */}
      {chartAvailable && !!futureChartSegments?.length && (
        <VictoryLine
          name="nowLine"
          data={[
            { x: intersectionX, y: -5 },
            { x: intersectionX, y: 120 },
          ]}
          style={{ data: { stroke: theme.colors.black, strokeWidth: 2, fillOpacity: 0.5 } }}
          labels={() => nowLabel}
          labelComponent={
            <VictoryLabel
              dy={7}
              backgroundPadding={{ top: 8, bottom: 6, left: 14, right: 14 }}
              backgroundStyle={{ fill: theme.colors.black }}
              style={{ fill: theme.colors.white, fontSize: 10, fontWeight: 'bold' }}
              backgroundComponent={<rect rx="2" ry="2" />}
            />
          }
        />
      )}

      {/* Transition markers: historic */}
      {chartAvailable && showBatteryActions && historicChartSegments.length > 0 && (
        <VictoryScatter
          name="historicTransitionMarkers"
          data={historicChartSegments
            .flatMap((segment, segmentIndex) => {
              if (segmentIndex === 0 || segment.data.length < 1) return [];
              const p = segment.data[0];
              return [{ x: p.x, y: p.y, _activityType: segment.activityType }];
            })
            .filter(Boolean)}
          size={3}
          style={{
            data: {
              fill: ({ datum }: { datum?: VictoryDatum }) => {
                const a = datum?._activityType ?? 'idle';
                if (a === 'idle' || a === 'solarCharge') return theme.colors.white;
                return theme.colors.batteryActivity[a] || theme.colors.off;
              },
              stroke: ({ datum }: { datum?: VictoryDatum }) =>
                theme.colors.batteryActivity[datum?._activityType ?? 'idle'] || theme.colors.off,
              strokeWidth: 1.5,
            },
          }}
        />
      )}

      {/* Transition markers: future */}
      {chartAvailable && !loopIsDisabled && showBatteryActions && (patchedFutureChartSegments?.length ?? 0) > 0 && (
        <VictoryScatter
          name="transitionMarkers"
          data={(patchedFutureChartSegments ?? [])
            .flatMap((segment, segmentIndex) => {
              if (segmentIndex === 0 || segment.data.length < 1) return [];
              const p = segment.data[0];
              return [{ x: p.x, y: p.y, _activityType: segment.activityType }];
            })
            .filter(Boolean)}
          size={3}
          style={{
            data: {
              fill: ({ datum }: { datum?: VictoryDatum }) => {
                const a = datum?._activityType ?? 'idle';
                if (a === 'idle' || a === 'solarCharge') return theme.colors.white;
                return theme.colors.batteryActivity[a] || theme.colors.off;
              },
              stroke: ({ datum }: { datum?: VictoryDatum }) =>
                theme.colors.batteryActivity[datum?._activityType ?? 'idle'] || theme.colors.off,
              strokeWidth: 1.5,
            },
          }}
        />
      )}

      {/* "Now" dot */}
      {chartAvailable && !!futureChartSegments?.length && (
        <VictoryScatter
          name="nowDot"
          data={[{ x: intersectionX, y: intersectionY, _isNow: true }]}
          size={4}
          style={{ data: { fill: theme.colors.secondary, stroke: theme.colors.white, strokeWidth: 2 } }}
        />
      )}

      {/* X-axis section labels */}
      {chartAvailable && !!futureChartSegments?.length && behaviorChartData?.length && (
        <>
          <VictoryLabel
            text="Previous 12 hours"
            x={prevLabelX}
            y={sectionLabelY}
            textAnchor="middle"
            style={{ fontSize: 10, fill: theme.colors.black, fontWeight: 'normal' }}
          />
          <VictoryLabel
            text="Next 12 hours"
            x={nextLabelX}
            y={sectionLabelY}
            textAnchor="middle"
            style={{ fontSize: 10, fill: theme.colors.black, fontWeight: 'normal' }}
          />
        </>
      )}
    </VictoryChart>
  );
};

export default SocChart;
